import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MaterialIcons } from '@expo/vector-icons';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { ProgressBackButton } from '@/components/progress/ProgressBackButton';
import { WebAppShell } from '@/design-system/components';
import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import { ICFProgressAPI } from '@/services/api/icfProgressAPI';
import { trackAuthEvent } from '@/services/analytics';
import { createSingleFlight } from '@/utils/singleFlight';
import { createIdempotencyIntent } from '@/services/idempotencyIntent';
import type {
  PracticeExamAttemptResponse,
  PracticeExamQuestion,
} from '@/types/icfProgress';

const OPTION_KEYS = ['a', 'b', 'c', 'd'] as const;
const COMPETENCY_LABELS: Record<string, string> = {
  C1: 'icfProgress.compEthics',
  C2: 'icfProgress.compMindset',
  C3: 'icfProgress.compAgreements',
  C4: 'icfProgress.compTrust',
  C5: 'icfProgress.compPresence',
  C6: 'icfProgress.compListening',
  C7: 'icfProgress.compAwareness',
  C8: 'icfProgress.compGrowth',
};

export default function PracticeExamScreen() {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState<PracticeExamQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<PracticeExamAttemptResponse | null>(null);
  // A double click on the last "Submit" sent two attempts (state is stale
  // within one frame); the lock flips synchronously.
  const submitLock = useRef(createSingleFlight()).current;
  // One Idempotency-Key per attempt: a retry after a network error reuses it
  // (the server returns the first result), "Try again" starts a new attempt.
  const attemptIntent = useRef(createIdempotencyIntent()).current;

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ICFProgressAPI.getPracticeExamQuestions();
      if (!response.questions.length) {
        throw new Error('empty');
      }
      setQuestions(response.questions);
      attemptIntent.reset();
      setAnswers({});
      setCurrentIndex(0);
      setResult(null);
      trackAuthEvent('practice_exam_started', { question_count: response.questions.length });
    } catch {
      setError(t('icfProgress.practiceExam.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadQuestions();
  }, [loadQuestions]);

  const currentQuestion = questions[currentIndex];
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;
  const questionById = useMemo(
    () => new Map(questions.map((question) => [question.id, question])),
    [questions],
  );

  const questionText = (questionId: string) => (
    t(`icfProgress.practiceExam.questions.${questionId}.prompt`)
  );
  const optionText = (questionId: string, optionIndex: number) => (
    t(`icfProgress.practiceExam.questions.${questionId}.options.${OPTION_KEYS[optionIndex]}`)
  );
  const competencyText = (competency: string) => t(
    COMPETENCY_LABELS[competency] || 'icfProgress.practiceExam.unknownCompetency',
  );

  const submitAttempt = () => submitLock.run(async () => {
    if (!currentQuestion || currentAnswer === undefined) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await ICFProgressAPI.submitPracticeExam(
        questions.map((question) => ({
          question_id: question.id,
          answer: answers[question.id],
        })),
        { idempotencyKey: attemptIntent.key() },
      );
      setResult(response);
      trackAuthEvent('practice_exam_completed', {
        question_count: response.total,
        correct_count: response.correct,
        percentage: response.percentage,
      });
    } catch {
      setError(t('icfProgress.practiceExam.submitError'));
    } finally {
      setSubmitting(false);
    }
  });

  const advance = () => {
    if (!currentQuestion || currentAnswer === undefined) return;
    if (currentIndex === questions.length - 1) {
      void submitAttempt();
      return;
    }
    setCurrentIndex((index) => index + 1);
  };

  const restart = () => {
    attemptIntent.reset();
    setAnswers({});
    setCurrentIndex(0);
    setResult(null);
    setError('');
    trackAuthEvent('practice_exam_restarted', { question_count: questions.length });
  };

  const content = (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      <ProgressBackButton />
      <View style={styles.heading}>
        <Text style={styles.title}>{t('icfProgress.practiceExam.title')}</Text>
        <Text style={styles.subtitle}>{t('icfProgress.practiceExam.subtitle')}</Text>
      </View>

      {loading ? (
        <View style={styles.statePanel}>
          <ActivityIndicator size="large" color={reviewDesk.accent} />
          <Text style={styles.stateText}>{t('icfProgress.practiceExam.loading')}</Text>
        </View>
      ) : error && questions.length === 0 ? (
        <View style={styles.statePanel}>
          <MaterialIcons name="error-outline" size={32} color="#B42318" />
          <Text style={styles.errorText}>{error}</Text>
          <Pressable onPress={() => void loadQuestions()} style={styles.primaryButton} accessibilityRole="button">
            <Text style={styles.primaryButtonText}>{t('icfProgress.practiceExam.retry')}</Text>
          </Pressable>
        </View>
      ) : result ? (
        <View style={styles.resultStack} testID="practice_exam_results">
          <View style={styles.scorePanel}>
            <View style={styles.scoreIcon}>
              <MaterialIcons name="school" size={30} color={reviewDesk.accent} />
            </View>
            <Text style={styles.resultEyebrow}>{t('icfProgress.practiceExam.resultsTitle')}</Text>
            <Text style={styles.scoreValue}>{result.percentage}%</Text>
            <Text style={styles.resultSummary}>
              {t('icfProgress.practiceExam.resultSummary', { correct: result.correct, total: result.total })}
            </Text>
            <Pressable onPress={restart} style={styles.primaryButton} accessibilityRole="button">
              <MaterialIcons name="refresh" size={18} color="#FFFFFF" />
              <Text style={styles.primaryButtonText}>{t('icfProgress.practiceExam.restart')}</Text>
            </Pressable>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>{t('icfProgress.practiceExam.competencyBreakdown')}</Text>
            <View style={styles.breakdownGrid}>
              {Object.entries(result.competency_scores).map(([competency, score]) => (
                <View key={competency} style={styles.breakdownItem}>
                  <Text style={styles.breakdownLabel}>{competencyText(competency)}</Text>
                  <Text style={styles.breakdownScore}>{score.correct}/{score.total}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>{t('icfProgress.practiceExam.reviewTitle')}</Text>
            {result.results.map((item, index) => {
              const question = questionById.get(item.question_id);
              if (!question) return null;
              return (
                <View key={item.question_id} style={styles.reviewItem}>
                  <View style={styles.reviewHeader}>
                    <View style={[styles.resultBadge, item.correct ? styles.correctBadge : styles.incorrectBadge]}>
                      <MaterialIcons
                        name={item.correct ? 'check' : 'close'}
                        size={15}
                        color={item.correct ? reviewDesk.success : '#B42318'}
                      />
                    </View>
                    <Text style={styles.reviewQuestion}>
                      {index + 1}. {questionText(item.question_id)}
                    </Text>
                  </View>
                  {!item.correct && (
                    <Text style={styles.correctAnswer}>
                      {t('icfProgress.practiceExam.correctAnswer')}: {optionText(item.question_id, item.correct_answer)}
                    </Text>
                  )}
                  <Text style={styles.explanation}>
                    {t(`icfProgress.practiceExam.questions.${item.question_id}.explanation`)}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>
      ) : currentQuestion ? (
        <View style={styles.examPanel} testID="practice_exam_question">
          <View style={styles.progressMeta}>
            <Text style={styles.progressText}>
              {t('icfProgress.practiceExam.progress', { current: currentIndex + 1, total: questions.length })}
            </Text>
            <Text style={styles.competencyPill}>{competencyText(currentQuestion.competency)}</Text>
          </View>
          <View style={styles.progressTrack}>
            <View style={[styles.progressFill, { width: `${((currentIndex + 1) / questions.length) * 100}%` }]} />
          </View>
          <Text style={styles.question}>{questionText(currentQuestion.id)}</Text>
          <View style={styles.options}>
            {OPTION_KEYS.slice(0, currentQuestion.option_count).map((optionKey, optionIndex) => {
              const selected = currentAnswer === optionIndex;
              return (
                <Pressable
                  key={optionKey}
                  testID={`practice_exam_option_${optionIndex}`}
                  accessibilityRole="radio"
                  accessibilityState={{ selected }}
                  onPress={() => setAnswers((current) => ({ ...current, [currentQuestion.id]: optionIndex }))}
                  style={({ pressed }) => [
                    styles.option,
                    selected && styles.optionSelected,
                    pressed && styles.pressed,
                  ]}
                >
                  <View style={[styles.optionLetter, selected && styles.optionLetterSelected]}>
                    <Text style={[styles.optionLetterText, selected && styles.optionLetterTextSelected]}>
                      {optionKey.toUpperCase()}
                    </Text>
                  </View>
                  <Text style={[styles.optionText, selected && styles.optionTextSelected]}>
                    {optionText(currentQuestion.id, optionIndex)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
          {!!error && <Text style={styles.errorText}>{error}</Text>}
          <View style={styles.actions}>
            {currentIndex > 0 && (
              <Pressable
                onPress={() => setCurrentIndex((index) => Math.max(0, index - 1))}
                style={styles.secondaryButton}
                accessibilityRole="button"
              >
                <MaterialIcons name="chevron-left" size={20} color={reviewDesk.accent} />
                <Text style={styles.secondaryButtonText}>{t('icfProgress.practiceExam.previous')}</Text>
              </Pressable>
            )}
            <Pressable
              testID="practice_exam_next"
              disabled={currentAnswer === undefined || submitting}
              onPress={advance}
              accessibilityRole="button"
              style={[styles.primaryButton, styles.nextButton, (currentAnswer === undefined || submitting) && styles.disabled]}
            >
              {submitting && <ActivityIndicator size="small" color="#FFFFFF" />}
              <Text style={styles.primaryButtonText}>
                {submitting
                  ? t('icfProgress.practiceExam.submitting')
                  : currentIndex === questions.length - 1
                    ? t('icfProgress.practiceExam.finish')
                    : t('icfProgress.practiceExam.next')}
              </Text>
              {!submitting && <MaterialIcons name="chevron-right" size={20} color="#FFFFFF" />}
            </Pressable>
          </View>
        </View>
      ) : null}
    </ScrollView>
  );

  return <WebAppShell active="progress" contentMaxWidth="100%" scroll={false}>{content}</WebAppShell>;
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: reviewDesk.canvas },
  content: { width: '100%', maxWidth: 920, alignSelf: 'center', padding: 0, paddingBottom: 80, gap: 20 },
  heading: { gap: 6 },
  title: { color: reviewDesk.ink, fontSize: 30, lineHeight: 37, fontWeight: '800' },
  subtitle: { color: reviewDesk.inkSoft, fontSize: 15, lineHeight: 22, maxWidth: 700 },
  statePanel: { minHeight: 300, borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, backgroundColor: reviewDesk.card, alignItems: 'center', justifyContent: 'center', padding: 28, gap: 14 },
  stateText: { color: reviewDesk.inkSoft, fontSize: 14 },
  errorText: { color: '#B42318', fontSize: 13, lineHeight: 19, textAlign: 'center' },
  examPanel: { borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, backgroundColor: reviewDesk.card, padding: 22, gap: 18 },
  progressMeta: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
  progressText: { color: reviewDesk.muted, fontSize: 12, fontWeight: '800' },
  competencyPill: { color: reviewDesk.accent, backgroundColor: reviewDesk.accentSoft, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5, fontSize: 12, fontWeight: '700' },
  progressTrack: { height: 5, borderRadius: 3, backgroundColor: reviewDesk.line, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 3, backgroundColor: reviewDesk.accent },
  question: { color: reviewDesk.ink, fontSize: 21, lineHeight: 29, fontWeight: '800' },
  options: { gap: 10 },
  option: { minHeight: 58, borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 13, backgroundColor: reviewDesk.card, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 12 },
  optionSelected: { borderColor: reviewDesk.accent, backgroundColor: reviewDesk.accentWash },
  optionLetter: { width: 32, height: 32, borderRadius: 16, backgroundColor: reviewDesk.cardMuted, alignItems: 'center', justifyContent: 'center' },
  optionLetterSelected: { backgroundColor: reviewDesk.accent },
  optionLetterText: { color: reviewDesk.inkSoft, fontSize: 13, fontWeight: '800' },
  optionLetterTextSelected: { color: '#FFFFFF' },
  optionText: { flex: 1, color: reviewDesk.inkSoft, fontSize: 14, lineHeight: 20 },
  optionTextSelected: { color: reviewDesk.ink, fontWeight: '600' },
  actions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10 },
  primaryButton: { minHeight: 42, borderRadius: 10, backgroundColor: reviewDesk.accent, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7 },
  primaryButtonText: { color: '#FFFFFF', fontSize: 13, fontWeight: '800' },
  nextButton: { minWidth: 124 },
  secondaryButton: { minHeight: 42, borderWidth: 1, borderColor: reviewDesk.lineStrong, borderRadius: 10, backgroundColor: reviewDesk.card, paddingHorizontal: 13, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 3 },
  secondaryButtonText: { color: reviewDesk.accent, fontSize: 13, fontWeight: '800' },
  disabled: { opacity: 0.42 },
  pressed: { opacity: 0.7 },
  resultStack: { gap: 16 },
  scorePanel: { borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, backgroundColor: reviewDesk.card, alignItems: 'center', padding: 26, gap: 8 },
  scoreIcon: { width: 58, height: 58, borderRadius: 29, backgroundColor: reviewDesk.accentSoft, alignItems: 'center', justifyContent: 'center', marginBottom: 3 },
  resultEyebrow: { color: reviewDesk.accent, fontSize: 12, fontWeight: '800', textTransform: 'uppercase' },
  scoreValue: { color: reviewDesk.ink, fontSize: 48, lineHeight: 56, fontWeight: '800', fontVariant: ['tabular-nums'] },
  resultSummary: { color: reviewDesk.inkSoft, fontSize: 15, marginBottom: 8 },
  panel: { borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, backgroundColor: reviewDesk.card, padding: 20, gap: 14 },
  panelTitle: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800' },
  breakdownGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  breakdownItem: { flexGrow: 1, minWidth: 180, borderRadius: 12, backgroundColor: reviewDesk.cardMuted, padding: 12, gap: 5 },
  breakdownLabel: { color: reviewDesk.inkSoft, fontSize: 12, lineHeight: 17, fontWeight: '600' },
  breakdownScore: { color: reviewDesk.accent, fontSize: 18, fontWeight: '800' },
  reviewItem: { borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 14, gap: 8 },
  reviewHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  resultBadge: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center' },
  correctBadge: { backgroundColor: reviewDesk.successSoft },
  incorrectBadge: { backgroundColor: '#FEECEB' },
  reviewQuestion: { flex: 1, color: reviewDesk.ink, fontSize: 14, lineHeight: 20, fontWeight: '700' },
  correctAnswer: { color: reviewDesk.success, fontSize: 13, lineHeight: 19, fontWeight: '700', marginLeft: 36 },
  explanation: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19, marginLeft: 36 },
});
